import { type Account, type AccountError, AccountService, ApiClient, DrawingClient } from '@acies/platform';
import { Effect, type Exit, Schema } from 'effect';
import type { IInventoryItemConfigurationReference, IInventoryItemReference, IModelReference } from '../domain/configurations';

/**
 * How long a cached answer is kept, and how long a failure is.
 *
 * An answer for as long as the page lives, a failure not at all. Both halves are deliberate: what the caches here hold does not change while someone is looking at it, and a call that failed is worth making again rather than being answered no from memory.
 *
 * Shared because the drawings and their stylesheets answer this the same way, and a cache that answered it differently would be a cache someone has to read to know how it behaves.
 */
export const keepAnswers = <A, E>(exit: Exit.Exit<A, E>): 'Infinity' | 0 => (exit._tag === 'Success' ? 'Infinity' : 0);

/**
 * The root the stores hang under, as the API names them to itself.
 *
 * Without the `/configurator` prefix, and that is not an oversight. The same store is reachable both ways, and which way is right depends on who is being told: the engine writes its own links prefixed, and the body that makes a configuration names a model unprefixed — the prefixed form is refused. See
 * {@link makeModelPath}.
 */
const appsRoot = '/apps/one/stores';

/**
 * The root the Configurator's own resources hang under, above the engine segment.
 *
 * Built from {@link appsRoot} rather than written out again, so the two roots cannot be changed apart and a reader can see at a glance that they are the same root.
 */
const storesRoot = `/configurator${appsRoot}`;

/** The root the BOM engine's resources hang under, above the engine segment. */
const bomRoot = '/bom/engines';

/**
 * Builds the path of a resource under one of the API's roots, naming the account's default engine.
 *
 * A function returning an Effect rather than a plain string, because the engine is read from the account per call: the host app rewrites it when the user switches account mid-session, exactly as the bearer token and the account id header are re-read per request.
 */
export type EnginePath = (resource: string) => Effect.Effect<string, AccountError>;

function makeEnginePath(root: string, account: Effect.Effect<Account, AccountError>): EnginePath {
	return (resource) => account.pipe(Effect.map(({ defaultEngine }) => `${root}/${defaultEngine}/${resource}`));
}

/**
 * Paths inside the store the Configurator keeps its own resources in.
 *
 * Built once in `ApiService.make` and handed to each resource's calls, so no resource file spells out `/configurator/apps/one/stores` itself.
 */
export function makeStorePath(account: Effect.Effect<Account, AccountError>): EnginePath {
	return makeEnginePath(storesRoot, account);
}

/**
 * Paths inside the BOM engine, which prices what the Configurator puts together.
 *
 * The same engine as the store, under a root of its own: the two are different services that happen to be keyed by the same identifier.
 */
export function makeBomPath(account: Effect.Effect<Account, AccountError>): EnginePath {
	return makeEnginePath(bomRoot, account);
}

/**
 * The root the drawing CDN keeps its stylesheets under.
 *
 * Only the stylesheets: a drawing itself sits at the origin's own root, and asking for one under this prefix answers 403. Two roots on one host is not something to work out from the outside, so it is written down once here.
 */
const filesRoot = '/files';

/**
 * Where the stylesheet every drawing is styled by sits.
 *
 * One file for everyone — no account, no engine — because it says what a frame, a sash and a measurement look like, and that is the same drawing to drawing.
 */
export const viewerStylePath = `${filesRoot}/svgviewer.css`;

/**
 * Where an account's own colours sit, whether or not it has any.
 *
 * A customer may upload a stylesheet that recolours its own drawings, and most have not. The path is the same either way — what differs is whether the CDN has anything at it — so this says where to look rather than whether it is worth looking.
 *
 * An Effect and not a plain string, for the same reason the API paths are: the engine and the account are read per call, since the host app rewrites both mid-session.
 */
export function makeColorStylePath(account: Effect.Effect<Account, AccountError>): Effect.Effect<string, AccountError> {
	return account.pipe(Effect.map(({ defaultEngine, id }) => `${filesRoot}/${id}/${defaultEngine}/color.css`));
}

/** How a construction model is named to something that is being told which model to build. */
export type ModelPath = (key: string) => Effect.Effect<IModelReference, AccountError>;

/**
 * Names one construction model the way a request body has to name it.
 *
 * The one path here that is written rather than read, and the one written without the `/configurator` prefix — see {@link appsRoot}. A model search answers with the prefixed form and the POST that makes a configuration refuses it, so handing a search result straight on is the mistake this exists to prevent. Its return
 * type is {@link IModelReference}, which means that mistake does not compile: the prefixed string is not assignable.
 *
 * The store is the account's default engine, the same identifier the store paths are keyed by.
 */
export function makeModelPath(account: Effect.Effect<Account, AccountError>): ModelPath {
	return (key) =>
		account.pipe(
			// annotated, and it has to be: the contextual type does not reach a callback through
			// Effect.map's inference, so without it this widens to string and the guarantee is lost
			Effect.map(({ defaultEngine }): IModelReference => `${appsRoot}/${defaultEngine}/construction-models/${key}`),
		);
}

/** How an inventory item, and the configuration it comes in when one has been picked, are named to something told which item to configure. */
export type StockItemPath = (item: string, itemConfiguration: string | null) => Effect.Effect<StockItemReference, AccountError>;

/** The two links a stock configuration is asked for with: the item, and the configuration it comes in — or null while none has been picked. */
export interface StockItemReference {
	readonly item: IInventoryItemReference;
	readonly itemConfiguration: IInventoryItemConfigurationReference | null;
}

/**
 * Names an inventory item and its configuration the way a request body has to name them.
 *
 * Written without the `/configurator` prefix, as {@link makeModelPath} writes a model, and for the same reason: the engine answers with the prefixed form and the POST is made with the plain one. The return type is what keeps them apart — the prefixed strings are not assignable to it.
 *
 * The store is the account's default engine, the same identifier the store paths are keyed by.
 */
export function makeStockItemPath(account: Effect.Effect<Account, AccountError>): StockItemPath {
	return (item, itemConfiguration) =>
		account.pipe(
			// annotated for the reason makeModelPath's callback is: the contextual type does not reach a
			// callback through Effect.map's inference, and the guarantee would widen to string
			Effect.map(({ defaultEngine }): StockItemReference => ({
				item: `${appsRoot}/${defaultEngine}/component-inventory-items/${item}`,
				itemConfiguration: itemConfiguration === null ? null : `${appsRoot}/${defaultEngine}/component-inventory-item-configurations/${itemConfiguration}`,
			})),
		);
}

/** Where the drawing of one calculation sits on the CDN, relative to the drawing origin. */
export type DrawingPath = (drawingId: string) => Effect.Effect<string, AccountError>;

/**
 * The object a drawing is served from, which the account names two thirds of.
 *
 * Not an {@link EnginePath}, and not under a root: the CDN has no resource segments, only the account, the engine and the calculation the drawing was made for. Read off the account per call for the same reason the API paths are — the host app rewrites it when the user switches account, and a captured account would go
 * on asking for another one's drawings.
 */
export function makeDrawingPath(account: Effect.Effect<Account, AccountError>): DrawingPath {
	return (drawingId) => account.pipe(Effect.map(({ defaultEngine, id }) => `/${id}/${defaultEngine}/${drawingId}.svg`));
}

/**
 * The envelope the Core API wraps every collection in.
 *
 * A `Schema.Struct` rather than a `Schema.Class`, because this is wire shape and nothing else: it carries no behaviour, is never constructed by hand, and never leaves the service — the calls unwrap it and hand back the items.
 *
 * The API also answers with `self`, `offset`, `limit`, `total` and `query`. Decoding drops them, the way `Account` drops the fields the Configurator does not use, so a contract break in a field nobody reads cannot fail a call that does not need it. `total` is one line away the day a caller needs to say how many
 * matches fell outside the limit.
 */
export function Collection<Item extends Schema.Top>(item: Item) {
	return Schema.Struct({ items: Schema.Array(item) });
}

/** What every resource's calls are built over. */
export interface CallDependencies {
	/** Paths inside the BOM engine the account's default engine names. */
	readonly bomPath: EnginePath;
	/** The client with this API's host, token, account header, auth failures and retries applied. */
	readonly client: typeof ApiClient.Service;
	/** The client with the public drawing origin applied, and deliberately nothing else. */
	readonly drawingClient: typeof DrawingClient.Service;
	/** Where the account's own drawing colours sit, if it has uploaded any. */
	readonly colorStylePath: Effect.Effect<string, AccountError>;
	/** How a construction model is named in a body that says which model to build. */
	readonly modelPath: ModelPath;
	/** Where a calculation's drawing sits, named by the account and its engine. */
	readonly drawingPath: DrawingPath;
	/** How an inventory item and its configuration are named in a body that says which item to configure. */
	readonly stockItemPath: StockItemPath;
	/** Paths inside the Configurator's store, named by the same engine. */
	readonly storePath: EnginePath;
}

/**
 * Acquires what every resource's calls are built over.
 *
 * Shared by `ApiService`, which assembles the calls for Effect-native callers, and by `runCall`, which runs a single call for a caller outside Effect. Both build the calls the same way because both start here.
 */
export const callDependencies: Effect.Effect<CallDependencies, never, AccountService | ApiClient | DrawingClient> = Effect.gen(function* () {
	const client = yield* ApiClient;
	const drawingClient = yield* DrawingClient;
	const { account } = yield* AccountService;
	return {
		bomPath: makeBomPath(account),
		client,
		colorStylePath: makeColorStylePath(account),
		drawingClient,
		drawingPath: makeDrawingPath(account),
		modelPath: makeModelPath(account),
		stockItemPath: makeStockItemPath(account),
		storePath: makeStorePath(account),
	};
});
